"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import { CTAButton } from "@/shadcn/ui/button";
import { PortableTextBlock } from "sanity";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";

export default function StepsSection({
  // data,
  className,
  chip,
  title,
  steps,
  cta,
}: {
  // data: PageChallengeSchema;
  chip: string;
  title: PortableTextBlock[];
  steps: Array<{
    color: string;
    bgColor: string;
    text: PortableTextBlock[];
    image: GROQImage;
  }>;
  cta: GROQButton;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative overflow-clip pb-[7rem] lg:pb-[20rem]",
        className,
      )}
    >
      <div className="container flex flex-col items-center relative">
        <ChipSerif text={chip} className="" />
        {title && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.4 }}
            className="richtext w-[min(105rem,95%)] prose-p:w-[min(75rem,100%)]"
          >
            <PortableText value={title} components={richTextComponent} />
          </motion.div>
        )}

        <div className="w-[min(190.25rem,100%)] grid md:grid-cols-2 lg:grid-cols-4 gap-[2.5rem] mt-[3rem] lg:mt-[4rem]">
          {steps &&
            steps.length &&
            steps.map((card, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 0.25,
                  delay: index * 0.08,
                  ease: [0.22, 0.61, 0.36, 1],
                }}
              >
                <div className="h-full flex flex-col gap-[4.5rem] rounded-[1.5rem] lg:rounded-[3rem] bg-white p-[2.25rem] ">
                  <div className="relative w-full">
                    <Image
                      src={card.image}
                      alt=""
                      className="!w-full rounded-[1.5rem]"
                    />
                    <span
                      className="absolute left-[2.15rem] bottom-0 translate-y-1/2 flex justify-center items-center rounded-[1.25rem] w-[7.5rem] h-[5rem] text-[2.25rem] font-[600]"
                      style={{ color: card.color, background: card.bgColor }}
                    >
                      0{index + 1}
                    </span>
                  </div>

                  {card?.text && (
                    <div className="richtext richtext-start prose-headings:text-[#103037] prose-headings:font-geist prose-headings:text-[2.75rem] prose-headings:font-medium prose-headings:tracking-[-0.09rem] [text-wrap-style:pretty]">
                      <PortableText
                        value={card.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
        </div>

        <motion.div
          className="mt-[4rem] lg:mt-[6rem] w-full lg:w-fit mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <CTAButton data={cta} />
        </motion.div>
      </div>
    </section>
  );
}
